import _ from "lodash";

import { GetAppOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { DEAL_IMPORT_SUPPORTED_FORMATS } from "utils/constants";
import { downloadFile } from "utils/file";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import Dialog, { DialogTitle } from "components/Dialog";
import FormControlLabel from "components/FormControlLabel";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";
import UploadButton from "components/UploadButton";

import { useCompanySettings } from "entities/Company/sdk";

import { DealImportFileUploadButton } from "./DealImportFileUploadButton";

export interface IDealImportFileUpload {
  selectedFile: File | null;
  handleFileSelect: React.Dispatch<React.SetStateAction<File | null>>;
  handleDealImportFileSubmit: () => void;
  open: boolean;
  handleClose: () => void;
  handleOnboardingChecklistCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  shouldAddOnboardingChecklist: boolean;
}

// TODO [@Georgi]: Is there a reason for this to be a separate component and not a part of the AddDealImportButton? Also - that component should be called DialogButton.
const DealImportFileUpload = ({
  handleFileSelect,
  selectedFile,
  handleDealImportFileSubmit,
  open,
  handleClose,
  shouldAddOnboardingChecklist,
  handleOnboardingChecklistCheckboxChange,
}: IDealImportFileUpload) => {
  const { data: companySettings } = useCompanySettings();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          margin: theme.spacing(2, 2, 0, 2),
          padding: 2,
          borderRadius: theme.spacing(1),
          backgroundColor: colors.blue20,
        }}
      >
        <Text variant="h1" fontWeight={700}>
          Bulk create deals: Import from Excel
        </Text>
      </DialogTitle>

      <Stack
        sx={{
          height: "350px",
          width: "860px",
          display: "grid",
          gridGap: theme.spacing(3),
          gridTemplateColumns: "1fr 1fr",
          padding: theme.spacing(4, 4, 0, 4),
        }}
      >
        <UploadButton
          onChange={({ files }: { files: Array<File> }) =>
            handleFileSelect(files[0])
          }
          accept={DEAL_IMPORT_SUPPORTED_FORMATS}
          maxFiles={1}
          renderComponent={(state) => (
            <Stack height="100%">
              <DealImportFileUploadButton state={state} />
            </Stack>
          )}
        />

        <Stack
          padding={theme.spacing(0, 2)}
          flexDirection="column"
          justifyContent={
            companySettings?.onboarding_company_package_shown
              ? "space-between"
              : "space-around"
          }
        >
          <Stack spacing={2}>
            <Text variant="h3">Import data from Excel</Text>
            <Text variant="text2">
              Upload an already filled out Excel sheet file to bulk create deals
              in our database.
            </Text>
            <Stack
              alignItems="center"
              flexDirection="row"
              gap={1}
              paddingTop={1}
              onClick={() =>
                downloadFile(
                  "/BulkDealImportTemplateFile.xlsx",
                  "BulkDealImportTemplateFile.xlsx"
                )
              }
              sx={{
                cursor: "pointer",
                width: "fit-content",
              }}
            >
              <GetAppOutlinedIcon
                sx={{
                  height: "24px",
                  width: "24px",
                  color: colors.gray60,
                }}
              />
              <Text fontWeight={500} color={colors.gray60}>
                DOWNLOAD EXCEL TEMPLATE
              </Text>
            </Stack>
          </Stack>
          <Stack spacing={1} justifyContent="space-between" direction="column">
            {!_.isNil(selectedFile) && (
              <>
                <Text
                  variant="text3"
                  fontWeight={500}
                  sx={{
                    backgroundColor: colors.gray20,
                    padding: theme.spacing(0.25, 0.5),
                    width: "fit-content",
                  }}
                >
                  UPLOADED FILE
                </Text>
                <Text variant="text2">{selectedFile.name}</Text>
              </>
            )}
          </Stack>
          {companySettings?.onboarding_company_package_shown && (
            <Tooltip
              title="Useful for closed deals where data can be automatically extracted from the closing documents with AI"
              placement="top-start"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleOnboardingChecklistCheckboxChange}
                    checked={shouldAddOnboardingChecklist}
                  />
                }
                label={<Text fontWeight={700}>Add onboarding checklist</Text>}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-end"
        padding={theme.spacing(0, 3, 3, 3)}
      >
        <Button onClick={handleClose} color="secondary" variant="text">
          Cancel
        </Button>
        <Button
          disabled={_.isNil(selectedFile)}
          onClick={handleDealImportFileSubmit}
          color="primary"
        >
          Process data & create deals
        </Button>
      </Stack>
    </Dialog>
  );
};
export default DealImportFileUpload;
